import React, {useEffect, useState} from 'react'
import {Box, CircularProgress} from '@mui/material'
import {Image} from 'antd'
import moment from 'moment'
import AppDialog from 'src/components-new/feedback/AppDialog'
import getFileMIMEtype from 'src/components-new/feedback/getFileMIMEtype'
import {BODY_PARTS_OPTIONS, STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'

interface ImageDialogNewProps {
  open: boolean
  onClose: () => void
  record: Record<string, any> | null
}

/**
 * Single-image viewer for a patient image row. Mirrors legacy ImageDialog,
 * except the binary is fetched through `apiClient` (so the mandatory `secret`
 * header is attached) and turned into a blob URL — a raw `<img src>` pointed
 * at `psp-backend/images/download/<file>` cannot carry that header and the
 * backend rejects it, leaving a broken-image icon.
 */
export default function ImageDialogNew({open, onClose, record}: ImageDialogNewProps): JSX.Element {
  const [status, setStatus] = useState<string>()
  const [imageUrl, setImageUrl] = useState<string>('')
  const bodyPartLabel = BODY_PARTS_OPTIONS.find(option => option.value === record?.bodyPart)?.label || record?.bodyPart || ''
  const title = record ? `${bodyPartLabel} - ${moment(record.date).format('DD MMM YYYY')}` : ''

  useEffect(() => {
    if (!open || !record?.file) {
      setImageUrl('')
      return undefined
    }

    let cancelled = false
    let objectUrl = ''

    apiClient.downloadPatientImage(record.file, setStatus).then(fileResponse => {
      if (cancelled || !fileResponse) return
      const mime = getFileMIMEtype(record.file) ?? 'image/jpeg'
      const blob = new Blob([new Uint8Array(fileResponse)], {type: mime})
      objectUrl = (window.URL || window.webkitURL).createObjectURL(blob)
      setImageUrl(objectUrl)
    })

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [open, record])

  return (
    <AppDialog open={open} onClose={onClose} title={title} maxWidth="lg">
      <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120, maxHeight: '70vh', overflow: 'hidden'}}>
        {status === STATUS.PENDING && !imageUrl && <CircularProgress />}
        {imageUrl && (
          <Image
            alt={title}
            preview={{zIndex: 9999}}
            src={imageUrl}
            style={{maxHeight: '70vh', maxWidth: '100%'}}
          />
        )}
      </Box>
    </AppDialog>
  )
}
